const PAD = 4
const RADIUS = 16
const ROW_TOLERANCE = 4
const MAX_WELD_GAP = 24

export interface PortraitBox {
    genre: string | null
    left: number
    top: number
    width: number
    height: number
}

export interface HighlightRegion {
    genre: string
    left: number
    top: number
    width: number
    height: number
    corners: {
        topLeft: number
        topRight: number
        bottomRight: number
        bottomLeft: number
    }
}

interface Rect {
    left: number
    top: number
    right: number
    bottom: number
}

function boxRect(box: PortraitBox): Rect {
    return {
        left: box.left,
        top: box.top,
        right: box.left + box.width,
        bottom: box.top + box.height,
    }
}

function union(rects: Rect[]): Rect {
    return {
        left: Math.min(...rects.map((rect) => rect.left)),
        top: Math.min(...rects.map((rect) => rect.top)),
        right: Math.max(...rects.map((rect) => rect.right)),
        bottom: Math.max(...rects.map((rect) => rect.bottom)),
    }
}

function overlapsX(a: Rect, b: Rect) {
    return a.left < b.right && b.left < a.right
}

function overlapsY(a: Rect, b: Rect) {
    return a.top < b.bottom && b.top < a.bottom
}

function gapBefore(edges: number[], edge: number) {
    if (edges.length === 0) return Infinity
    return edge - Math.max(...edges)
}

function gapAfter(edges: number[], edge: number) {
    if (edges.length === 0) return Infinity
    return Math.min(...edges) - edge
}

function inset(gap: number) {
    if (!Number.isFinite(gap)) return PAD
    return Math.min(PAD, Math.max(0, gap / 2 - 1))
}

function grow(rect: Rect, others: Rect[]): Rect {
    const leftGap = gapBefore(
        others.filter((other) => other.right <= rect.left + 0.5 && overlapsY(rect, other)).map((other) => other.right),
        rect.left,
    )
    const rightGap = gapAfter(
        others.filter((other) => other.left >= rect.right - 0.5 && overlapsY(rect, other)).map((other) => other.left),
        rect.right,
    )
    const topGap = gapBefore(
        others.filter((other) => other.bottom <= rect.top + 0.5 && overlapsX(rect, other)).map((other) => other.bottom),
        rect.top,
    )
    const bottomGap = gapAfter(
        others.filter((other) => other.top >= rect.bottom - 0.5 && overlapsX(rect, other)).map((other) => other.top),
        rect.bottom,
    )
    return {
        left: rect.left - inset(leftGap),
        right: rect.right + inset(rightGap),
        top: rect.top - inset(topGap),
        bottom: rect.bottom + inset(bottomGap),
    }
}

function sameRow(a: PortraitBox, b: PortraitBox) {
    return Math.abs(a.top - b.top) <= ROW_TOLERANCE
}

/** Width the genre tag may use: every portrait of that genre on the tag's row. */
export function genreTagWidths(portraits: PortraitBox[]): number[] {
    const widths = portraits.map(() => 0)
    let index = 0
    while (index < portraits.length) {
        const genre = portraits[index].genre
        if (!genre || portraits[index].width <= 0 || portraits[index].height <= 0) {
            index += 1
            continue
        }
        let end = index + 1
        while (end < portraits.length && portraits[end].genre === genre) end += 1
        let last = index
        for (let cursor = index + 1; cursor < end; cursor += 1) {
            if (!sameRow(portraits[index], portraits[cursor]) || portraits[cursor].width <= 0) break
            last = cursor
        }
        widths[index] = portraits[last].left + portraits[last].width - portraits[index].left
        index = end
    }
    return widths
}

function regionFrom(genre: string, rect: Rect, corners: HighlightRegion['corners']): HighlightRegion {
    return {
        genre,
        left: rect.left,
        top: rect.top,
        width: rect.right - rect.left,
        height: rect.bottom - rect.top,
        corners,
    }
}

/** A welded neighbor covers this corner, so the radius would open a gap. */
function cornerJoins(neighbor: Rect | null, edge: number, atStart: boolean) {
    if (!neighbor) return false
    if (atStart) return neighbor.left <= edge + 1 && neighbor.right > edge + 1
    return neighbor.right >= edge - 1 && neighbor.left < edge - 1
}

/**
 * Backgrounds for contiguous genre runs. A run that wraps is welded across
 * rows so the highlight follows the portraits instead of moving them.
 */
export function highlightRegions(portraits: PortraitBox[]): HighlightRegion[] {
    const visible = portraits.filter((portrait) => portrait.width > 0 && portrait.height > 0)
    const regions: HighlightRegion[] = []
    let index = 0
    while (index < visible.length) {
        const genre = visible[index].genre
        if (!genre) {
            index += 1
            continue
        }
        let end = index + 1
        while (end < visible.length && visible[end].genre === genre) end += 1
        const run = visible.slice(index, end)
        const rows: PortraitBox[][] = []
        run.forEach((portrait) => {
            const row = rows[rows.length - 1]
            if (!row || !sameRow(row[0], portrait)) rows.push([portrait])
            else row.push(portrait)
        })
        const outside = visible.filter((portrait) => !run.includes(portrait)).map(boxRect)
        const grown = rows.map((row) => grow(union(row.map(boxRect)), outside))
        const weldedToNext = grown.map(() => false)
        for (let rowIndex = 0; rowIndex < grown.length - 1; rowIndex += 1) {
            const upper = grown[rowIndex]
            const lower = grown[rowIndex + 1]
            const gap = lower.top - upper.bottom
            if (gap > MAX_WELD_GAP) continue
            const mid = upper.bottom + gap / 2
            upper.bottom = mid + 0.5
            lower.top = mid - 0.5
            weldedToNext[rowIndex] = true
        }
        grown.forEach((rect, rowIndex) => {
            const above = rowIndex > 0 && weldedToNext[rowIndex - 1] ? grown[rowIndex - 1] : null
            const below = weldedToNext[rowIndex] ? grown[rowIndex + 1] : null
            regions.push(regionFrom(genre, rect, {
                topLeft: cornerJoins(above, rect.left, true) ? 0 : RADIUS,
                topRight: cornerJoins(above, rect.right, false) ? 0 : RADIUS,
                bottomLeft: cornerJoins(below, rect.left, true) ? 0 : RADIUS,
                bottomRight: cornerJoins(below, rect.right, false) ? 0 : RADIUS,
            }))
        })
        index = end
    }
    return regions
}
